/**
 * Generic datasheet list for the Cash Flows hub pages (Access
 * `frmReportCashFlowMain` subforms). One reusable grid: TableVirtuoso +
 * global search + per-column Excel filter + pagination + row-select toggle +
 * frozen first column + numeric Total footer. Each hub page passes its own
 * columns/rows/getRowId; the Frame owns the toolbar.
 */
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { type CSSProperties, type ReactNode, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'
import BrandState from '../../../components/BrandState'
import ResizeHandle from '../../../components/ResizeHandle'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  filterBarBtnSx,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
} from '../../../components/dataList'
import { useColumnWidths } from '../../../hooks/useColumnWidths'
import { useIsMobile } from '../../../hooks/useResponsive'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../../theme/responsive'
import { getTableSortSuffix, sortTableRows, type TableSortState, toggleTableSort } from '../../../utils/tableSort'
import { titleCaseName } from '../../../utils/text'

export type CashflowColumnKind = 'text' | 'date' | 'datetime' | 'number'

export interface CashflowColumn<T> {
  key: keyof T & string
  label: string
  kind?: CashflowColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  /** Default column width (px). User resize (useColumnWidths) still overrides. */
  width?: number
  /** Long text → wrap + row auto-grows (gold list `/sales/contract/list`). */
  wrap?: boolean
  /** Title-case the cell value (proper names stored UPPERCASE in DB). Display-only. */
  proper?: boolean
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

interface Props<T> {
  columns: CashflowColumn<T>[]
  rows: T[]
  getRowId: (row: T) => string | number
  loading: boolean
  fetching: boolean
  error: boolean
  errorTitle: string
  emptyTitle: string
  searchPlaceholder: string
  selectable?: boolean
  selectedId: string | number | null
  onSelect: (id: string | number | null) => void
  /** Toolbar buttons (New/Edit/…) rendered in the FilterBar rightSlot. */
  toolbar?: ReactNode
  /** Left-side slot before the Search filter (e.g. the "Go to Page" selector). */
  leftSlot?: ReactNode
  /** Open the Columns menu — renders a "Columns" button next to Search. */
  onOpenColumns?: (anchor: HTMLElement) => void
  /** Optional info line (e.g. Bank "Checked Time"). */
  info?: ReactNode
}

export default function CashflowDataList<T>({
  columns,
  rows: baseRows,
  getRowId,
  loading,
  fetching,
  error,
  errorTitle,
  emptyTitle,
  searchPlaceholder,
  selectable = true,
  selectedId,
  onSelect,
  toolbar,
  leftSlot,
  onOpenColumns,
  info,
}: Props<T>) {
  const isMobile = useIsMobile()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof T> | null>(null)
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{ key: keyof T & string; position: ColumnFilterAnchorPosition } | null>(null)
  const { getWidth, startResize } = useColumnWidths('report.cashflow')

  const frozenKey = columns[0]?.key
  const FROZEN_FALLBACK = isMobile ? 130 : 120

  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row as never, searchTerm, columns as never) &&
          rowMatchesAllColumnFilters(row as never, columnFilters.filters),
      ),
    [baseRows, columns, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(
    () => sortTableRows(filteredRows as never, sortState as never, columns as never) as T[],
    [filteredRows, sortState, columns],
  )
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const totalRowValues = useMemo(() => {
    const m = new Map<string, number | null>()
    for (const col of columns) {
      if (col.kind !== 'number') continue
      let total = 0
      let has = false
      for (const r of filteredRows) {
        const v = (r as Record<string, unknown>)[col.key]
        if (typeof v === 'number' && !Number.isNaN(v)) {
          total += v
          has = true
        }
      }
      m.set(col.key, has ? total : null)
    }
    return m
  }, [filteredRows, columns])

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const minTableWidth = Math.max(1100, columns.length * 110)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${columns.length}`

  const popoverColumn = useMemo(() => (colPopover ? columns.find((c) => c.key === colPopover.key) ?? null : null), [colPopover, columns])
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows as never) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows as never, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }
  const toggleSort = (key: keyof T) => {
    setSortState((cur) => toggleTableSort(cur as never, key as never) as never)
    setPage(0)
  }

  const cellValue = (row: T, col: CashflowColumn<T>): string => {
    const val = (row as Record<string, unknown>)[col.key]
    if (val == null) return ''
    if (col.kind === 'date' || col.kind === 'datetime') return formatAccessDate(val)
    if (typeof val === 'number') return formatAccessNumber(val)
    if (col.proper) return titleCaseName(val)
    return String(val)
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        showLabel={false}
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            {toolbar}
          </Stack>
        }
      >
        {leftSlot}
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => filterPopover.open('search', e)} />
        {onOpenColumns && (
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => onOpenColumns(e.currentTarget)}
            sx={filterBarBtnSx}
            title="Columns"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>
              Columns
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
              Cols
            </Box>
          </Button>
        )}
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{ display: 'flex', justifyContent: info ? 'space-between' : 'flex-end', alignItems: 'center', gap: 1, minHeight: 30, py: 0.125 }}
        >
          {info && (
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              {info}
            </Typography>
          )}
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(loading || fetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[50, 100, 200, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v)
                setPage(0)
              }}
              placeholder={searchPlaceholder}
              label="Search"
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState tone="error" title={errorTitle} description="Please refresh the page or contact the system administrator." />
          ) : loading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState title={emptyTitle} description="Try adjusting the table filters above." />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {columns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = key === frozenKey
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) {
                      headerStyle.width = w
                      headerStyle.minWidth = w
                      headerStyle.maxWidth = w
                    }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', ...(isFrozen ? {} : { position: 'relative' }) }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState as never, col.key as never)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={
                totalRowValues.size > 0
                  ? () => (
                      <TableRow>
                        {columns.map((col, index) => {
                          const key = String(col.key)
                          const isFrozen = key === frozenKey
                          const total = totalRowValues.get(col.key)
                          const val = total == null ? '' : formatAccessNumber(total)
                          const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK : undefined)
                          return (
                            <TableCell
                              key={key}
                              align={col.align}
                              className={isFrozen ? 'app-sticky-col' : undefined}
                              style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                              sx={{
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                py: 0.5,
                                bgcolor: 'background.paper',
                                borderTop: (t) => `1px solid ${t.palette.divider}`,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {index === 0 ? 'Total' : val}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    )
                  : undefined
              }
              itemContent={(_: number, row: T) =>
                columns.map((col) => {
                  const key = String(col.key)
                  const isFrozen = key === frozenKey
                  const val = cellValue(row, col)
                  const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK : undefined)
                  // WRAP: cột flag `wrap` HOẶC user đã resize → chữ dài xuống dòng,
                  // dòng tự cao (TableVirtuoso đo động). Mirror `/sales/contract/list`.
                  const wrap = col.wrap || getWidth(key) != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={val || '-'}
                        sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const row = Number.isFinite(idx) ? rows[idx] : null
                  const id = row != null ? getRowId(row) : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={selectable && id != null && id === selectedId}
                      sx={{ cursor: selectable ? 'pointer' : 'default' }}
                      onClick={() => {
                        if (!selectable || id == null) return
                        onSelect(id === selectedId ? null : id)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

